/**
 * The money in a POST-booking seat purchase.
 *
 * Two seat concepts exist on a booking and they must never be summed together:
 *
 *   `seat_selections_detail` — seats picked on the map during checkout. Financed
 *     INSIDE the installment plan (they join the held subtotal and carry both
 *     fees), so they belong in the Cost Breakdown's plan math. See
 *     src/utils/seatTotalFromFlightData.js.
 *
 *   `seat_purchase` — seats bought AFTER booking, through /api/seat-purchase.
 *     Charged on their own Stripe PaymentIntent with a flat 5% processing fee
 *     and settled in full. This money never touches the installment plan, so
 *     folding it into the plan total would invent a charge the plan doesn't
 *     carry — which is exactly why the breakdown excludes it.
 *
 * This module covers the second. It exists because that money was previously
 * read by no financial surface at all: not Net Margin, not Analytics, not
 * Accounting, not Investor, not Risk Review. The customer's payment and the
 * airline's seat cost were both invisible.
 *
 * ─── Why both sides must move together ──────────────────────────────────────
 *
 * A post-booking seat has revenue AND cost:
 *
 *     customer paid   total_cents          (seat + fee, one Stripe charge)
 *     airline seat    seat_total_cents     (pass-through, Defera owes this)
 *     Defera keeps    processing_fee_cents (the 5%)
 *
 * Today `_actual_cost` is set by an admin at ticketing, which usually happens
 * long before a post-booking seat is bought — N2DUA2 was ticketed in April and
 * the seat bought in August. So neither side is currently counted and they
 * roughly cancel. That cancellation is an accident, not a design: the moment
 * someone updates Actual Cost to include a seat, margin drops by the seat price
 * while the offsetting revenue is still missing, and the trip reads worse than
 * it is.
 *
 * So every consumer must add revenue and subtract cost TOGETHER. Adding only
 * the revenue would be the same bug pointing the other way.
 */

const centsToDollars = (cents) => {
  const n = Number(cents);
  return Number.isFinite(n) ? n / 100 : 0;
};

/**
 * Extract the economics of a booking's post-booking seat purchase.
 *
 * Free seats skip Stripe entirely and still write a `seat_purchase` row with
 * zero money — those are real seat assignments worth showing, but they must not
 * move any financial line. `hasMoney` is the flag every money consumer should
 * branch on; `exists` is for surfaces that care about the assignment itself.
 *
 * @param {object|null|undefined} flightData a booking's `flight_data`
 * @returns {{
 *   exists: boolean,
 *   hasMoney: boolean,
 *   revenue: number,
 *   airlineCost: number,
 *   deferaFee: number,
 *   netToDefera: number,
 *   seatCount: number,
 *   purchasedAt: string|null,
 * }} dollars throughout
 */
export function seatPurchaseEconomics(flightData) {
  const sp = flightData?.seat_purchase;
  const empty = {
    exists: false,
    hasMoney: false,
    revenue: 0,
    airlineCost: 0,
    deferaFee: 0,
    netToDefera: 0,
    seatCount: 0,
    purchasedAt: null,
  };
  if (!sp || typeof sp !== 'object') return empty;

  const revenue = centsToDollars(sp.total_cents);
  const airlineCost = centsToDollars(sp.seat_total_cents);
  // Prefer the stored fee, but fall back to the difference so a legacy row
  // written before processing_fee_cents existed still reconciles.
  const storedFee = centsToDollars(sp.processing_fee_cents);
  const deferaFee = storedFee > 0 ? storedFee : Math.max(0, revenue - airlineCost);

  return {
    exists: true,
    // Guard on revenue, not on the row existing: a free-seat purchase is a real
    // assignment but zero money, and must not nudge any margin.
    hasMoney: revenue > 0,
    revenue,
    airlineCost,
    deferaFee,
    // What the trip's P&L should actually move by. Equals the 5% fee in the
    // normal case; derived rather than assumed so an odd row can't silently
    // report a profit it didn't make.
    netToDefera: revenue - airlineCost,
    seatCount: Array.isArray(sp.seats) ? sp.seats.length : 0,
    purchasedAt: typeof sp.purchased_at === 'string' ? sp.purchased_at : null,
  };
}

/**
 * The single term to fold into a trip's net margin.
 *
 * Returns revenue MINUS airline cost, so callers cannot accidentally book the
 * customer's payment as profit while leaving the pass-through cost behind — the
 * asymmetry this module exists to prevent. Safe to add unconditionally: it is 0
 * for every booking without a paid seat purchase.
 *
 * @param {object|null|undefined} flightData
 * @returns {number} dollars, positive when Defera kept something
 */
export function seatPurchaseMarginContribution(flightData) {
  const e = seatPurchaseEconomics(flightData);
  return e.hasMoney ? e.netToDefera : 0;
}
